import type { EnhancedAnalyticsData } from '../../types';
import type { MessageCallbacks } from './types';
import { normalizeTimestamp } from './types';

/** Convert the VPS analytics wire format to the client EnhancedAnalyticsData shape. */
export function normalizeAnalytics(obj: any): EnhancedAnalyticsData {
  const a = obj.analytics;
  return {
    depthImbalance: a.depth || a.depthImbalance ? {
      topN: a.depth?.topN || a.depthImbalance?.topN || 5,
      bidVolume: a.depth?.bidVolume || a.depthImbalance?.bidVolume || 0,
      askVolume: a.depth?.askVolume || a.depthImbalance?.askVolume || 0,
      imbalance: a.depth?.imbalance || a.depthImbalance?.imbalance || 0,
      imbalancePercent: a.depth?.imbalancePercent || a.depthImbalance?.imbalancePercent || 0,
    } : { topN: 5, bidVolume: 0, askVolume: 0, imbalance: 0, imbalancePercent: 0 },

    microprice: a.microprice ? {
      microprice: a.microprice.value || a.microprice.microprice || 0,
      bidPrice: a.microprice.bidPrice || 0,
      askPrice: a.microprice.askPrice || 0,
      bidSize: a.microprice.bidSize || 0,
      askSize: a.microprice.askSize || 0,
      spread: a.microprice.spread || 0,
    } : { microprice: 0, bidPrice: 0, askPrice: 0, bidSize: 0, askSize: 0, spread: 0 },

    staleness: a.staleness ? {
      lastUpdateAge: a.staleness.lastUpdateAge || 0,
      lastTradeAge: a.staleness.lastTradeAge || 0,
      isStale: a.staleness.isStale || false,
      stalenessLevel: a.staleness.stalenessLevel || 'fresh',
    } : { lastUpdateAge: 0, lastTradeAge: 0, isStale: false, stalenessLevel: 'fresh' },

    orderFlowImbalance: a.orderFlow ? {
      buyFlow: a.orderFlow.buyFlow || 0,
      sellFlow: a.orderFlow.sellFlow || 0,
      netFlow: a.orderFlow.netFlow || 0,
      flowRatio: a.orderFlow.flowRatio || 0,
      timeWindow: a.orderFlow.timeWindow || 300,
    } : { buyFlow: 0, sellFlow: 0, netFlow: 0, flowRatio: 0, timeWindow: 300 },

    fillProbability: a.fillProbability ? {
      probability: a.fillProbability.probability || 0,
      queuePosition: a.fillProbability.queuePosition || 0,
      estimatedWaitTime: a.fillProbability.estimatedWaitTime || 0,
      confidence: a.fillProbability.confidence || 'low',
      bidProbability: a.fillProbability.bidProbability,
      askProbability: a.fillProbability.askProbability,
      bidQueuePosition: a.fillProbability.bidQueuePosition,
      askQueuePosition: a.fillProbability.askQueuePosition,
    } : { probability: 0, queuePosition: 0, estimatedWaitTime: 0, confidence: 'low' },

    volatility: a.volatility ? {
      realizedVol: a.volatility.realizedVol || 0,
      impliedVol: a.volatility.impliedVol || 0,
      volatilityPercentile: a.volatility.volatilityPercentile || 0,
      recommendedSpread: a.volatility.recommendedSpread || 0,
      spreadMultiplier: a.volatility.spreadMultiplier || 1.2,
    } : { realizedVol: 0, impliedVol: 0, volatilityPercentile: 0, recommendedSpread: 0, spreadMultiplier: 1.2 },

    liquidityHeatmap: (() => {
      const lh = a.liquidityHeatmap;
      if (!lh) return { priceLevels: [], maxLiquidity: 0, avgLiquidity: 0 };
      const levels: Array<{ price: number; side: 'bid' | 'ask'; size: number }> = [];
      if (Array.isArray(lh.bids)) lh.bids.forEach((b: any) => levels.push({ price: Number(b?.price ?? 0), side: 'bid', size: Number(b?.size ?? 0) }));
      if (Array.isArray(lh.asks)) lh.asks.forEach((a2: any) => levels.push({ price: Number(a2?.price ?? 0), side: 'ask', size: Number(a2?.size ?? 0) }));
      const totalLiq = Number(lh.totalBidLiquidity ?? 0) + Number(lh.totalAskLiquidity ?? 0) || levels.reduce((s, l) => s + l.size, 0);
      return {
        priceLevels: (lh.levels || levels).map((level: any) => ({
          price: level.price || 0,
          bidLiquidity: level.side === 'bid' ? (level.size || 0) : 0,
          askLiquidity: level.side === 'ask' ? (level.size || 0) : 0,
          totalLiquidity: level.size || 0,
          heatScore: 0.5,
        })),
        maxLiquidity: lh.totalLiquidity ?? totalLiq,
        avgLiquidity: totalLiq && levels.length ? totalLiq / levels.length : 0,
      };
    })(),

    vwapAnalysis: a.vwap ? {
      currentVWAP: a.vwap.value || a.vwap.currentVWAP || 0,
      distance: a.vwap.distance || 0,
      distancePercent: a.vwap.distancePercent || 0,
      volume: a.vwap.volume || 0,
      trend: a.vwap.trend || 'neutral',
    } : { currentVWAP: 0, distance: 0, distancePercent: 0, volume: 0, trend: 'neutral' },

    bookPressure: a.bookPressure ? {
      pressure: a.bookPressure.pressure || 0,
      buyPressure: a.bookPressure.buyPressure || 0,
      sellPressure: a.bookPressure.sellPressure || 0,
      netPressure: a.bookPressure.netPressure || 0,
      confidenceLevel: a.bookPressure.confidenceLevel || 'low',
    } : { pressure: 0, buyPressure: 0, sellPressure: 0, netPressure: 0, confidenceLevel: 'low' },

    spreadAnalysis: a.spread ? {
      currentSpread: a.spread.currentSpread || 0,
      averageSpread: a.spread.averageSpread || 0,
      spreadPercentile: a.spread.spreadPercentile || 0,
      isWide: a.spread.isWide || false,
      competitiveness: a.spread.competitiveness || 'normal',
      optimalBid: a.spread.optimalBid || 0,
      optimalAsk: a.spread.optimalAsk || 0,
    } : { currentSpread: 0, averageSpread: 0, spreadPercentile: 0, isWide: false, competitiveness: 'normal', optimalBid: 0, optimalAsk: 0 },

    riskMetrics: a.risk ? {
      inventoryRisk: a.risk.inventoryRisk || 0,
      priceRisk: a.risk.priceRisk || 0,
      liquidityRisk: a.risk.liquidityRisk || 0,
      overallRisk: (() => {
        const r = a.risk.overallRisk;
        if (typeof r === 'string') {
          const l = r.toLowerCase();
          if (l === 'low' || l === 'medium' || l === 'high') return l as 'low' | 'medium' | 'high';
        }
        if (typeof r === 'number') return r >= 0.7 ? 'high' : r >= 0.4 ? 'medium' : 'low';
        return 'low';
      })(),
      maxExposure: a.risk.maxExposure || 0,
      recommendedPosition: a.risk.recommendedPosition || 0,
    } : { inventoryRisk: 0, priceRisk: 0, liquidityRisk: 0, overallRisk: 'low', maxExposure: 0, recommendedPosition: 0 },

    fullBookImbalance: a.fullBookImbalance || a.depth?.fullBookImbalance ? {
      bidVolume: a.fullBookImbalance?.bidVolume || a.depth?.fullBookImbalance?.bidVolume || 0,
      askVolume: a.fullBookImbalance?.askVolume || a.depth?.fullBookImbalance?.askVolume || 0,
      imbalance: a.fullBookImbalance?.imbalance || a.depth?.fullBookImbalance?.imbalance || 0,
      imbalancePercent: a.fullBookImbalance?.imbalancePercent || a.depth?.fullBookImbalance?.imbalancePercent || 0,
    } : { bidVolume: 0, askVolume: 0, imbalance: 0, imbalancePercent: 0 },

    ml: a.ml ? {
      enabled: a.ml.enabled === true,
      fill_probability: a.ml.fill_probability ? {
        buy: {
          at_best_ask: a.ml.fill_probability.buy?.at_best_ask || 0,
          at_mid: a.ml.fill_probability.buy?.at_mid || 0,
          at_best_bid: a.ml.fill_probability.buy?.at_best_bid || 0,
        },
        sell: {
          at_best_bid: a.ml.fill_probability.sell?.at_best_bid || 0,
          at_mid: a.ml.fill_probability.sell?.at_mid || 0,
          at_best_ask: a.ml.fill_probability.sell?.at_best_ask || 0,
        },
        details: {
          buy_at_ask: a.ml.fill_probability.details?.buy_at_ask
            ? {
                probability: a.ml.fill_probability.details.buy_at_ask.probability || 0,
                confidence: (['low', 'medium', 'high'].includes(a.ml.fill_probability.details.buy_at_ask.confidence))
                  ? a.ml.fill_probability.details.buy_at_ask.confidence as 'low' | 'medium' | 'high'
                  : 'low',
                estimated_wait_time: a.ml.fill_probability.details.buy_at_ask.estimated_wait_time || 0,
                queue_position: a.ml.fill_probability.details.buy_at_ask.queue_position || 0,
                model_version: a.ml.fill_probability.details.buy_at_ask.model_version || '',
                model_type: a.ml.fill_probability.details.buy_at_ask.model_type || '',
                timestamp: a.ml.fill_probability.details.buy_at_ask.timestamp || '',
              }
            : { probability: 0, confidence: 'low' as const, estimated_wait_time: 0, queue_position: 0, model_version: '', model_type: '', timestamp: '' },
          sell_at_bid: a.ml.fill_probability.details?.sell_at_bid
            ? {
                probability: a.ml.fill_probability.details.sell_at_bid.probability || 0,
                confidence: (['low', 'medium', 'high'].includes(a.ml.fill_probability.details.sell_at_bid.confidence))
                  ? a.ml.fill_probability.details.sell_at_bid.confidence as 'low' | 'medium' | 'high'
                  : 'low',
                estimated_wait_time: a.ml.fill_probability.details.sell_at_bid.estimated_wait_time || 0,
                queue_position: a.ml.fill_probability.details.sell_at_bid.queue_position || 0,
                model_version: a.ml.fill_probability.details.sell_at_bid.model_version || '',
                model_type: a.ml.fill_probability.details.sell_at_bid.model_type || '',
                timestamp: a.ml.fill_probability.details.sell_at_bid.timestamp || '',
              }
            : { probability: 0, confidence: 'low' as const, estimated_wait_time: 0, queue_position: 0, model_version: '', model_type: '', timestamp: '' },
        },
      } : undefined,
      regime: a.ml.regime ? {
        regime: (['trending', 'neutral', 'volatile', 'sideways'].includes(a.ml.regime.regime))
          ? a.ml.regime.regime as 'trending' | 'neutral' | 'volatile' | 'sideways'
          : 'neutral',
        confidence: a.ml.regime.confidence || 0,
        regime_features: {
          spread_asymmetry: a.ml.regime.regime_features?.spread_asymmetry || 0,
          aggression_factor: a.ml.regime.regime_features?.aggression_factor || 1.0,
          optimal_spread_multiplier: a.ml.regime.regime_features?.optimal_spread_multiplier || 1.0,
        },
        model_version: a.ml.regime.model_version || '',
        timestamp: a.ml.regime.timestamp || '',
      } : undefined,
      error: a.ml.error,
      reason: a.ml.reason,
    } : undefined,
    timestamp: Date.now(),
  } as EnhancedAnalyticsData;
}

export function handleAnalytics(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'ANALYTICS' && obj.secId && obj.analytics)) return false;

  const secId = (obj.secId || '').toUpperCase();
  const ts = normalizeTimestamp(obj.timestamp);
  const analyticsData = normalizeAnalytics(obj);

  // Accumulate depth-imbalance + OFI history for ALL symbols (no re-render)
  const depthImb = analyticsData.depthImbalance.imbalance;
  const { buyFlow, sellFlow } = analyticsData.orderFlowImbalance;
  const total = buyFlow + sellFlow;
  const ofi = total > 0 ? (buyFlow - sellFlow) / total : 0;
  const tMs = analyticsData.timestamp || Date.now();
  const existing = cb.analyticsHistoryRef.current.get(secId) ?? [];
  const updated = [...existing, { t: tMs, depthImbalance: depthImb, ofi }];
  // Cap at 5000 points per symbol to prevent unbounded memory growth
  cb.analyticsHistoryRef.current.set(secId, updated.length > 5000 ? updated.slice(-5000) : updated);

  // Cache latest analytics for ALL symbols (used for instant prefetch on symbol switch)
  cb.lastAnalyticsBySecIdRef.current.set(secId, analyticsData);

  if (secId === cb.selectedSymbol) {
    cb.setAnalytics(prev => {
      const merged = { ...analyticsData };
      if (prev?.ml?.enabled || prev?.ml?.fill_probability || prev?.ml?.regime) {
        merged.ml = prev.ml;
      }
      const lastMlUpdateMs = cb.lastMlFillUpdateMsBySecIdRef.current.get(secId) ?? 0;
      const mlFillIsFresh = lastMlUpdateMs > 0 && (Date.now() - lastMlUpdateMs) < 5000;
      if (prev?.fillProbability && prev.fillProbability.probability > 0
        && (mlFillIsFresh || !analyticsData.fillProbability || analyticsData.fillProbability.probability === 0)) {
        merged.fillProbability = prev.fillProbability;
      }
      if (prev?.dseSignals && (prev.dseSignals.ofi !== 0 || prev.dseSignals.true_ofi !== 0)) {
        merged.orderFlowImbalance = prev.orderFlowImbalance;
      }
      return merged;
    });
    cb.setLastUpdateTime(ts);
  }

  cb.lastUpdateBySecIdRef.current.set(secId, ts);
  return true;
}
